import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material'
import get from 'lodash/get'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useActions } from '../hooks/useActions'
import { useLocalization } from '../redux/reduxHooks'
import { getLabel, pathToPerson } from '../utils/helpers'

const renderMenuItems = (valueRange, dateType) => {
  const options = [...Array(valueRange[1] - valueRange[0] + 1)].map((_, i) =>
    (valueRange[0] + i).toString(),
  )
  // reverse options if years
  if (dateType === 'year') {
    options.reverse()
  }

  return options.map((option) => (
    <MenuItem key={option} value={option}>
      {option}
    </MenuItem>
  ))
}

export const DOBInputs = ({ value, label, dateType, valueRange, onChange, disabled }) => {
  return (
    <FormControl key={'dob-' + dateType} fullWidth>
      <InputLabel
        disabled={disabled}
        sx={(theme) => ({
          color: theme.palette.primary.main,
          fontWeight: '500',
        })}
      >
        {label}
      </InputLabel>
      <Select
        label={label}
        disabled={disabled}
        value={value ?? ''}
        onChange={onChange}
        inputProps={{ 'aria-label': dateType }}
        // limits the height of the dropdown
        MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
      >
        {renderMenuItems(valueRange, dateType)}
      </Select>
    </FormControl>
  )
}

const DateOfBirth = ({ id }) => {
  const [isDateError, setIsDateError] = useState(false)
  const localization = useLocalization()
  const dateOfBirth = useSelector((state) => get(state, [...pathToPerson(id), 'dateOfBirth'])) || {}
  const isUK = useSelector((state) => get(state, ['questionnaire', 'countryCode'])) === 'GBR'
  const isProband = useSelector((state) => get(state, ['questionnaire', 'probandId'])) === id
  const { setPersonDateOfBirth } = useActions()

  const { day, month, year } = dateOfBirth
  const inputProps = [
    {
      value: year,
      label: 'Year',
      dateType: 'year',
      max: new Date().getFullYear(),
    },
    { value: month, label: 'Month', dateType: 'month', max: 12 },
    { value: day, label: 'Day', dateType: 'day', max: getDays() },
  ]

  if (isUK) {
    inputProps.reverse()
  }

  return (
    <Box>
      <FormLabel>{getLabel(localization, 'dateOfBirth')}</FormLabel>
      {isDateError && (
        <Typography color="error" variant="subtitle2">
          Invalid entry, please enter a valid date
        </Typography>
      )}
      <Box display="flex" gap={1} mt={2}>
        {inputProps.map(({ value, label, dateType, max }) => (
          <TextField
            key={label}
            type="number"
            value={value ? parseInt(value) : ''}
            inputProps={{ min: 1, max, 'aria-label': label }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault()
            }}
            onChange={({ target: { value } }) => {
              if (value > max || value < 0) {
                setIsDateError(true)

                return
              }
              setIsDateError(false)
              setPersonDateOfBirth({ id, dateType, value })
              validateDay({ dateType, value })
            }}
            label={<Typography>{label}</Typography>}
            autoComplete="off"
            disabled={isProband}
            sx={{ maxWidth: 100 }}
          />
        ))}
      </Box>
    </Box>
  )
  // update day if month/year is updated and it is out of range
  function validateDay({ dateType, value }) {
    const isYear = dateType === 'year'
    const daysInMonth = new Date(isYear ? value : year, isYear ? month : value, 0).getDate()
    if (parseInt(day) > daysInMonth) {
      setPersonDateOfBirth({ id, dateType: 'day', value: daysInMonth })
    }
  }
  // get the number of days in the month selected
  function getDays() {
    const daysInMonth = new Date(year || 2000, month, 0).getDate()

    return daysInMonth || 31
  }
}

export default DateOfBirth
